import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { DRAFT_KEYS, loadDraft, saveDraft, clearDraft } from '../../../../utils/draftUtils';
import { useNotification } from '../../../../contexts/NotificationContext';

export const useOperationSheet = (uhid_no, navigate) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    uhid_no: uhid_no || "",
    pre_op_diagnosis: '',
    type_of_procedure: '',
    post_op_diagnosis: '',
    name_of_procedure: '',
    operation_date: '',
    operation_start_time: '',
    duration_of_procedure: '',
    name_of_surgeon: '',
    assisted_by: '',
    name_of_anesthesiologist: '',
    scrubbed_nurse: '',
    type_of_anesthesia: '',
    circulating_nurse: '',
    operation_notes: '',
    findings: '',
    signature: {
      surgeon: '',
      anesthesiologist: '',
      scrub_nurse: ''
    }
  });

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSigner, setCurrentSigner] = useState("");
  const signaturePadRef = useRef(null);

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);
      setFormData(prev => ({
        ...prev,
        patient_name: response.data.name,
        age: response.data.age,
        sex: response.data.sex,
      }));
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormRecord = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_operation_sheet/${uhid_no}`);
      if (response.data) {
        const data = response.data;
        
        const formatDateTime = (val) => val ? new Date(val).toISOString().slice(0, 16) : '';
        const formatDate = (val) => val ? new Date(val).toISOString().slice(0, 10) : '';

        const durationMap = {
            "15": "15 minutes", "30": "30 minutes", "45": "45 minutes", "60": "1 hour",
            "90": "1.5 hours", "120": "2 hours", "150": "2.5 hours", "180": "3 hours", "999": "More than 3 hours"
        };
        
        setFormData(prev => ({
          ...prev,
          ...data,
          operation_start_time: formatDateTime(data.operation_start_time),
          operation_date: formatDate(data.operation_date),
          duration_of_procedure: durationMap[data.duration_of_procedure] || data.duration_of_procedure,
        }));
        setSuccess("success");
      }
    } catch (error) {
      console.error('Error fetching operation sheet record:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchPatientData(uhid_no);
      await fetchFormRecord();
      setDataLoaded(true);
      setLoading(false);
    };
    init();
  }, [uhid_no]);

  // Draft handles
  useEffect(() => {
    if (!dataLoaded) return;
    const draft = loadDraft(DRAFT_KEYS.OPERATION_SHEET, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no, dataLoaded]);

  useEffect(() => {
    if (!dataLoaded) return;
    const timeoutId = setTimeout(() => {
      saveDraft(DRAFT_KEYS.OPERATION_SHEET, uhid_no, formData);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded, uhid_no]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSignatureOpen = (signer) => {
    setCurrentSigner(signer);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData(prev => ({
      ...prev,
      signature: { ...prev.signature, [currentSigner]: dataURL }
    }));
    setSignatureDialogOpen(false);
  };

  const validate = () => {
    const tempErrors = {};
    const required = [
        'pre_op_diagnosis', 'type_of_procedure', 'post_op_diagnosis', 
        'name_of_procedure', 'operation_date', 'operation_start_time', 
        'duration_of_procedure', 'name_of_surgeon', 'name_of_anesthesiologist', 
        'scrubbed_nurse', 'type_of_anesthesia'
    ];
    required.forEach(field => { if (!formData[field]) tempErrors[field] = "Required"; });
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validate()) {
      showNotification('error', "Please fill in all required fields.");
      return;
    }

    // Map duration back to backend values if needed or just send the label
    const dataToSend = { ...formData };
    
    try {
      setLoading(true);
      if (success === "success") {
        await axios.put(`${API_BASE_URL}/update_operation_sheet/${uhid_no}`, dataToSend);
        showNotification('success', 'Operation Sheet updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/operation_sheet`, dataToSend);
        showNotification('success', 'Operation Sheet submitted successfully!');
      }
      clearDraft(DRAFT_KEYS.OPERATION_SHEET, uhid_no);
      navigate(`/mainPage/${uhid_no}`, { state: { from: 'IntraOperativePhase', status: 'success', step: 2 } });
    } catch (error) {
      console.error("Submission error:", error);
      showNotification('error', "Error submitting operation sheet.");
    } finally {
      setLoading(false);
    }
  };

  return {
    formData, setFormData, errors, loading, success,
    signatureDialogOpen, setSignatureDialogOpen, signaturePadRef,
    handleChange, handleSignatureOpen, handleSignatureSave, handleSubmit
  };
};
